import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import ConversationV2 from '../ConversationV2'

import { arrayDifference } from '../../../../utilities'
import { getLatestEventDate } from './helpers'
import { selectViews } from '../../../../slices/views'
import {
  MobileScreen,
  selectConvo,
  setMobileScreen
} from '../../../../slices/convo'
import { useQueueState } from '../../../../providers/QueueStateProvider'
import useStyles from './styles'

const ConversationListV2 = ({ mobile }) => {
  const dispatch = useAppDispatch()

  const { views } = useAppSelector(selectViews)
  const { view } = useAppSelector(selectConvo)
  const { conversations } = useAppSelector(selectConvo)

  const { id: selected, handleSelect: handleConvoSelect } = useQueueState()

  const showingClosed = React.useMemo(() => {
    if (!view) {
      return false
    }

    const selectedView = views.find(v => v.id === view)
    if (selectedView.filter.state) {
      return selectedView.filter.state.includes('CLOSED')
    }

    return false
  }, [view, views])

  const all = React.useMemo(() => {
    const list = Object.values(conversations)

    // Sort by latest event, newest towards the start of the list
    const sorted = list.sort((a, b) => {
      const aLatest = getLatestEventDate(a)
      const bLatest = getLatestEventDate(b)
      return bLatest - aLatest
    })

    const filtered = sorted.filter((session, i) => {
      // Find index of first session with the same owner
      const firstSiblingIdx = sorted.findIndex(
        otherSess => otherSess.owner_id === session.owner_id
      )

      // Keep in the list if this is the first, remove otherwise
      return firstSiblingIdx === i
    })

    // Add session with same owners to each object
    const withRelated = filtered.map(session => {
      const related = sorted.filter(
        otherSession => otherSession.owner_id === session.owner_id
      )

      return { convo: session, related }
    })

    return withRelated
  }, [conversations])

  const viewable = React.useMemo(() => {
    if (showingClosed) {
      return all
    }

    return all.filter(({ convo }) => convo.state !== 'CLOSED')
  }, [showingClosed, all])

  const [allIDs, viewablIDs] = React.useMemo(() => {
    const getID = ({ convo }) => convo.session
    const allIDs = Object.values(all).map(getID)
    const viewablIDs = Object.values(viewable).map(getID)
    return [allIDs, viewablIDs]
  }, [all, viewable])

  const classes = useStyles({ mobile })

  // Select the next conversation on the list if the current one is closed
  React.useEffect(() => {
    const diff = arrayDifference(allIDs, viewablIDs)
    if (!diff.includes(selected)) {
      return
    }

    const findNextID = () => {
      const idx = allIDs.findIndex(x => x === selected)

      let offset = 1

      // Try to find the next going down the list
      while (idx + offset < allIDs.length) {
        const val = allIDs[idx + offset]
        if (viewablIDs.includes(val)) {
          return val
        }
        offset++
      }

      offset = -1

      // Try to find the next going up the list
      while (idx + offset > -1) {
        const val = allIDs[idx + offset]
        if (viewablIDs.includes(val)) {
          return val
        }
        offset--
      }

      return null
    }

    const nextID = findNextID()
    if (nextID) {
      handleConvoSelect(nextID)
      dispatch(setMobileScreen(MobileScreen.Conversation))
    } else {
      handleConvoSelect('')
      dispatch(setMobileScreen(MobileScreen.Queue))
    }
  }, [allIDs, viewablIDs, selected, dispatch, handleConvoSelect])

  return (
    <div className={classes.root}>
      {viewable.length === 0 ? (
        <div className={classes.empty}>
          No chats have come into this inbox yet.
        </div>
      ) : (
        viewable.map((session, i) => (
          <ConversationV2
            key={`${session.convo.id}-${i}`}
            i={i}
            conversation={session.convo}
            related={session.related}
            mobile={mobile}
          />
        ))
      )}
    </div>
  )
}
export default ConversationListV2
